/**
 * Compliance Dashboard Page
 *
 * Platform-level compliance monitoring and approval workflows for superadmins
 * Tracks usage reconciliation, anomaly detection, pending approvals, and system health
 *
 * Requires special permission: platform.compliance.view
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useComplianceStore } from '@/stores/complianceStore';
import {
  UsageResetRequest,
  BulkAssignRequest,
  PricingChangeNotification,
  AnomalyAlert,
  CustomPlan,
  PartnerQuota,
  ReconciliationStatus
} from '@/services/compliance';
import { DataTable, ColumnDefinition } from '@/components/shared/DataTable';
import { PageTransition, PageTransitionItem } from '@/components/ui/PageTransition';
import { AnimatedCard, AnimatedCardContent } from '@/components/ui/AnimatedCard';
import {
  ShieldCheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ChartBarIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  DocumentCheckIcon,
  BuildingOfficeIcon,
  BoltIcon,
  EyeIcon,
  XMarkIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BellAlertIcon,
  ScaleIcon,
  Cog6ToothIcon,
  CalendarDaysIcon,
  PlayIcon
} from '@heroicons/react/24/outline';

// ============================================================================
// TYPES
// ============================================================================

type TabType = 'overview' | 'approvals' | 'anomalies' | 'custom-plans' | 'quotas' | 'reconciliation' | 'pricing';

interface ModalState {
  type: 'approve-reset' | 'reject-reset' | 'approve-bulk' | 'reject-bulk' | 'anomaly-detail' | 'trigger-reconciliation' | null;
  data?: UsageResetRequest | BulkAssignRequest | AnomalyAlert | null;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ComplianceDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [modal, setModal] = useState<ModalState>({ type: null, data: null });
  const [rejectReason, setRejectReason] = useState('');
  const [reconciliationPeriod, setReconciliationPeriod] = useState('');

  const {
    dashboard,
    dashboardLoading,
    pendingApprovals,
    approvalsLoading,
    anomalies,
    anomaliesSummary,
    anomaliesLoading,
    customPlans,
    customPlansSummary,
    customPlansLoading,
    partnerQuotas,
    quotasSummary,
    quotasLoading,
    reconciliationStatus,
    reconciliationLoading,
    pricingChanges,
    pricingChangesSummary,
    pricingChangesLoading,
    fetchDashboard,
    fetchPendingApprovals,
    approveUsageReset,
    rejectUsageReset,
    approveBulkAssign,
    rejectBulkAssign,
    fetchAnomalies,
    fetchCustomPlans,
    fetchPartnerQuotas,
    fetchReconciliationStatus,
    triggerReconciliation,
    fetchPricingChanges
  } = useComplianceStore();

  // Initial data load
  useEffect(() => {
    fetchDashboard();
    fetchPendingApprovals();
  }, [fetchDashboard, fetchPendingApprovals]);

  // Load tab-specific data
  useEffect(() => {
    switch (activeTab) {
      case 'anomalies':
        fetchAnomalies();
        break;
      case 'custom-plans':
        fetchCustomPlans();
        break;
      case 'quotas':
        fetchPartnerQuotas();
        break;
      case 'reconciliation':
        fetchReconciliationStatus();
        break;
      case 'pricing':
        fetchPricingChanges();
        break;
    }
  }, [activeTab, fetchAnomalies, fetchCustomPlans, fetchPartnerQuotas, fetchReconciliationStatus, fetchPricingChanges]);

  // Handlers
  const handleApproveReset = useCallback(async () => {
    if (modal.type === 'approve-reset' && modal.data) {
      const request = modal.data as UsageResetRequest;
      const success = await approveUsageReset(request.id);
      if (success) {
        setModal({ type: null, data: null });
      }
    }
  }, [modal, approveUsageReset]);

  const handleRejectReset = useCallback(async () => {
    if (modal.type === 'reject-reset' && modal.data && rejectReason.trim()) {
      const request = modal.data as UsageResetRequest;
      const success = await rejectUsageReset(request.id, rejectReason.trim());
      if (success) {
        setModal({ type: null, data: null });
        setRejectReason('');
      }
    }
  }, [modal, rejectReason, rejectUsageReset]);

  const handleApproveBulk = useCallback(async () => {
    if (modal.type === 'approve-bulk' && modal.data) {
      const request = modal.data as BulkAssignRequest;
      const success = await approveBulkAssign(request.id);
      if (success) {
        setModal({ type: null, data: null });
      }
    }
  }, [modal, approveBulkAssign]);

  const handleRejectBulk = useCallback(async () => {
    if (modal.type === 'reject-bulk' && modal.data && rejectReason.trim()) {
      const request = modal.data as BulkAssignRequest;
      const success = await rejectBulkAssign(request.id, rejectReason.trim());
      if (success) {
        setModal({ type: null, data: null });
        setRejectReason('');
      }
    }
  }, [modal, rejectReason, rejectBulkAssign]);

  const handleTriggerReconciliation = useCallback(async () => {
    const success = await triggerReconciliation(reconciliationPeriod || undefined);
    if (success) {
      setModal({ type: null, data: null });
      setReconciliationPeriod('');
    }
  }, [reconciliationPeriod, triggerReconciliation]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const getReconciliationStatusColor = (status: ReconciliationStatus) => {
    switch (status) {
      case 'match':
        return 'text-green-600 bg-green-100';
      case 'minor_variance':
        return 'text-amber-600 bg-amber-100';
      case 'major_variance':
        return 'text-orange-600 bg-orange-100';
      case 'critical':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getSeverityColor = (severity: 'critical' | 'warning' | 'info') => {
    switch (severity) {
      case 'critical':
        return 'text-red-600 bg-red-100';
      case 'warning':
        return 'text-amber-600 bg-amber-100';
      case 'info':
        return 'text-blue-600 bg-blue-100';
    }
  };

  // Loading state
  if (dashboardLoading && !dashboard) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center h-64">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          >
            <div className="h-12 w-12 border-4 border-purple-600 border-t-transparent rounded-full" />
          </motion.div>
        </div>
      </PageTransition>
    );
  }

  const tabs: { id: TabType; label: string; icon: React.ReactNode; badge?: number }[] = [
    { id: 'overview', label: 'Overview', icon: <ChartBarIcon className="h-4 w-4" /> },
    {
      id: 'approvals',
      label: 'Pending Approvals',
      icon: <DocumentCheckIcon className="h-4 w-4" />,
      badge: dashboard?.pendingApprovals.total
    },
    {
      id: 'anomalies',
      label: 'Anomalies',
      icon: <ExclamationTriangleIcon className="h-4 w-4" />,
      badge: anomaliesSummary.critical > 0 ? anomaliesSummary.critical : undefined
    },
    {
      id: 'custom-plans',
      label: 'Custom Plans',
      icon: <Cog6ToothIcon className="h-4 w-4" />,
      badge: customPlansSummary.needsReview > 0 ? customPlansSummary.needsReview : undefined
    },
    {
      id: 'quotas',
      label: 'Partner Quotas',
      icon: <UserGroupIcon className="h-4 w-4" />,
      badge: quotasSummary.atLimit > 0 ? quotasSummary.atLimit : undefined
    },
    { id: 'reconciliation', label: 'Reconciliation', icon: <ScaleIcon className="h-4 w-4" /> },
    {
      id: 'pricing',
      label: 'Pricing Changes',
      icon: <CurrencyDollarIcon className="h-4 w-4" />,
      badge: pricingChangesSummary.pending > 0 ? pricingChangesSummary.pending : undefined
    }
  ];

  return (
    <PageTransition className="space-y-6" stagger staggerDelay={0.05}>
      {/* Header */}
      <PageTransitionItem>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShieldCheckIcon className="h-7 w-7 text-purple-600" />
              Compliance Dashboard
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Monitor system integrity, approve critical operations, and track compliance
            </p>
          </div>
          <div className="flex items-center gap-3">
            <motion.button
              onClick={() => {
                fetchDashboard();
                fetchPendingApprovals();
              }}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <ArrowPathIcon className={`h-4 w-4 ${dashboardLoading ? 'animate-spin' : ''}`} />
              Refresh
            </motion.button>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <ClockIcon className="h-4 w-4" />
              {dashboard?.timestamp ? formatDate(dashboard.timestamp) : 'Loading...'}
            </div>
          </div>
        </div>
      </PageTransitionItem>

      {/* Quick Stats */}
      <PageTransitionItem>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <StatCard
            label="Pending Approvals"
            value={dashboard?.pendingApprovals.total || 0}
            icon={<DocumentCheckIcon className="h-4 w-4" />}
            color={dashboard?.pendingApprovals.total ? 'amber' : 'green'}
          />
          <StatCard
            label="Reconciliation"
            value={reconciliationLabel(dashboard?.reconciliation.status)}
            icon={<ScaleIcon className="h-4 w-4" />}
            color={reconciliationColor(dashboard?.reconciliation.status)}
            isText
          />
          <StatCard
            label="Custom Plans"
            value={dashboard?.customPlans.totalActive || 0}
            icon={<Cog6ToothIcon className="h-4 w-4" />}
            color="purple"
            subtitle={dashboard?.customPlans.needingReview ? `${dashboard.customPlans.needingReview} need review` : undefined}
          />
          <StatCard
            label="Partners Near Quota"
            value={dashboard?.quotaHealth.partnersNearLimit || 0}
            icon={<UserGroupIcon className="h-4 w-4" />}
            color={dashboard?.quotaHealth.partnersNearLimit ? 'amber' : 'green'}
          />
          <StatCard
            label="Usage Resets"
            value={dashboard?.pendingApprovals.usageResets || 0}
            icon={<ArrowPathIcon className="h-4 w-4" />}
            color={dashboard?.pendingApprovals.usageResets ? 'amber' : 'gray'}
          />
          <StatCard
            label="Bulk Assigns"
            value={dashboard?.pendingApprovals.bulkAssigns || 0}
            icon={<BuildingOfficeIcon className="h-4 w-4" />}
            color={dashboard?.pendingApprovals.bulkAssigns ? 'amber' : 'gray'}
          />
          <StatCard
            label="Price Changes"
            value={dashboard?.pendingApprovals.pricingChanges || 0}
            icon={<CurrencyDollarIcon className="h-4 w-4" />}
            color={dashboard?.pendingApprovals.pricingChanges ? 'amber' : 'gray'}
          />
        </div>
      </PageTransitionItem>

      {/* Tabs */}
      <PageTransitionItem>
        <div className="border-b border-gray-200">
          <nav className="flex space-x-4 overflow-x-auto pb-px" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors
                  ${activeTab === tab.id
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {tab.icon}
                {tab.label}
                {tab.badge !== undefined && tab.badge > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-xs font-bold rounded-full bg-red-100 text-red-700">
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </PageTransitionItem>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'overview' && (
            <OverviewTab
              dashboard={dashboard}
              reconciliationStatus={reconciliationStatus}
              anomaliesSummary={anomaliesSummary}
              customPlansSummary={customPlansSummary}
              quotasSummary={quotasSummary}
              pricingChangesSummary={pricingChangesSummary}
              onTabChange={setActiveTab}
            />
          )}

          {activeTab === 'approvals' && (
            <ApprovalsTab
              pendingApprovals={pendingApprovals}
              loading={approvalsLoading}
              onApproveReset={(req) => setModal({ type: 'approve-reset', data: req })}
              onRejectReset={(req) => setModal({ type: 'reject-reset', data: req })}
              onApproveBulk={(req) => setModal({ type: 'approve-bulk', data: req })}
              onRejectBulk={(req) => setModal({ type: 'reject-bulk', data: req })}
              formatDate={formatDate}
            />
          )}

          {activeTab === 'anomalies' && (
            <AnomaliesTab
              anomalies={anomalies}
              summary={anomaliesSummary}
              loading={anomaliesLoading}
              onViewDetail={(alert) => setModal({ type: 'anomaly-detail', data: alert })}
              getSeverityColor={getSeverityColor}
              formatDate={formatDate}
            />
          )}

          {activeTab === 'custom-plans' && (
            <CustomPlansTab
              plans={customPlans}
              summary={customPlansSummary}
              loading={customPlansLoading}
              formatDate={formatDateShort}
            />
          )}

          {activeTab === 'quotas' && (
            <PartnerQuotasTab
              quotas={partnerQuotas}
              summary={quotasSummary}
              loading={quotasLoading}
            />
          )}

          {activeTab === 'reconciliation' && (
            <ReconciliationTab
              status={reconciliationStatus}
              loading={reconciliationLoading}
              onTrigger={() => setModal({ type: 'trigger-reconciliation', data: null })}
              getStatusColor={getReconciliationStatusColor}
              formatDate={formatDate}
            />
          )}

          {activeTab === 'pricing' && (
            <PricingChangesTab
              changes={pricingChanges}
              summary={pricingChangesSummary}
              loading={pricingChangesLoading}
              formatDate={formatDate}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Modals */}
      <AnimatePresence>
        {modal.type === 'approve-reset' && modal.data && (
          <ConfirmModal
            title="Approve Usage Reset"
            message={`Are you sure you want to approve this usage reset request for "${(modal.data as UsageResetRequest).organizationId?.name}"?`}
            confirmLabel="Approve"
            confirmVariant="success"
            onConfirm={handleApproveReset}
            onCancel={() => setModal({ type: null, data: null })}
          />
        )}

        {modal.type === 'reject-reset' && modal.data && (
          <RejectModal
            title="Reject Usage Reset"
            message={`Please provide a reason for rejecting this usage reset request.`}
            reason={rejectReason}
            onReasonChange={setRejectReason}
            onConfirm={handleRejectReset}
            onCancel={() => {
              setModal({ type: null, data: null });
              setRejectReason('');
            }}
          />
        )}

        {modal.type === 'approve-bulk' && modal.data && (
          <ConfirmModal
            title="Approve Bulk Assignment"
            message={`Are you sure you want to approve this bulk assignment for ${(modal.data as BulkAssignRequest).organizations?.length || 0} organizations?`}
            confirmLabel="Approve"
            confirmVariant="success"
            onConfirm={handleApproveBulk}
            onCancel={() => setModal({ type: null, data: null })}
          />
        )}

        {modal.type === 'reject-bulk' && modal.data && (
          <RejectModal
            title="Reject Bulk Assignment"
            message={`Please provide a reason for rejecting this bulk assignment request.`}
            reason={rejectReason}
            onReasonChange={setRejectReason}
            onConfirm={handleRejectBulk}
            onCancel={() => {
              setModal({ type: null, data: null });
              setRejectReason('');
            }}
          />
        )}

        {modal.type === 'anomaly-detail' && modal.data && (
          <AnomalyDetailModal
            alert={modal.data as AnomalyAlert}
            onClose={() => setModal({ type: null, data: null })}
            getSeverityColor={getSeverityColor}
          />
        )}

        {modal.type === 'trigger-reconciliation' && (
          <TriggerReconciliationModal
            period={reconciliationPeriod}
            onPeriodChange={setReconciliationPeriod}
            onConfirm={handleTriggerReconciliation}
            onCancel={() => {
              setModal({ type: null, data: null });
              setReconciliationPeriod('');
            }}
            isRunning={reconciliationStatus?.isRunning || false}
          />
        )}
      </AnimatePresence>
    </PageTransition>
  );
};

// ============================================================================
// SUB-COMPONENTS: STAT CARD
// ============================================================================

interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  isText?: boolean;
  subtitle?: string;
}

// The reconciliation tile shows a word, not the raw status value. A job that has not run yet is
// not a variance, so it is gray rather than amber.
const RECONCILIATION_LABELS: Record<ReconciliationStatus, string> = {
  match: 'OK',
  minor_variance: 'Minor variance',
  major_variance: 'Major variance',
  critical: 'Critical',
  never_run: 'Never run',
};

const reconciliationLabel = (status?: ReconciliationStatus): string =>
  (status && RECONCILIATION_LABELS[status]) || 'N/A';

const reconciliationColor = (status?: ReconciliationStatus): string => {
  if (status === 'match') return 'green';
  if (status === 'critical') return 'red';
  if (!status || status === 'never_run') return 'gray';
  return 'amber';
};

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color, isText, subtitle }) => {
  const colorClasses: Record<string, string> = {
    green: 'bg-green-100 text-green-600',
    amber: 'bg-amber-100 text-amber-600',
    red: 'bg-red-100 text-red-600',
    purple: 'bg-purple-100 text-purple-600',
    blue: 'bg-blue-100 text-blue-600',
    gray: 'bg-gray-100 text-gray-600'
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-2 mb-1">
        <div className={`p-1.5 rounded-md ${colorClasses[color] || colorClasses.gray}`}>
          {icon}
        </div>
      </div>
      <p className={`text-2xl font-bold ${color === 'red' ? 'text-red-600' : color === 'amber' ? 'text-amber-600' : 'text-gray-900'}`}>
        {isText ? value : (typeof value === 'number' ? value.toLocaleString() : value)}
      </p>
      <p className="text-xs text-gray-500">{label}</p>
      {subtitle && <p className="text-xs text-amber-600 mt-1">{subtitle}</p>}
    </div>
  );
};

// ============================================================================
// TAB: OVERVIEW
// ============================================================================

interface OverviewTabProps {
  dashboard: ReturnType<typeof useComplianceStore>['dashboard'];
  reconciliationStatus: ReturnType<typeof useComplianceStore>['reconciliationStatus'];
  anomaliesSummary: ReturnType<typeof useComplianceStore>['anomaliesSummary'];
  customPlansSummary: ReturnType<typeof useComplianceStore>['customPlansSummary'];
  quotasSummary: ReturnType<typeof useComplianceStore>['quotasSummary'];
  pricingChangesSummary: ReturnType<typeof useComplianceStore>['pricingChangesSummary'];
  onTabChange: (tab: TabType) => void;
}

const OverviewTab: React.FC<OverviewTabProps> = ({
  dashboard,
  reconciliationStatus,
  anomaliesSummary,
  customPlansSummary,
  quotasSummary,
  pricingChangesSummary,
  onTabChange
}) => {
  const totalIssues =
    (dashboard?.pendingApprovals.total || 0) +
    (anomaliesSummary.critical || 0) +
    (customPlansSummary.expired || 0) +
    (quotasSummary.atLimit || 0);

  const complianceScore = Math.max(0, 100 - (totalIssues * 5));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Compliance Score */}
      <AnimatedCard variant="elevated" className="lg:col-span-1">
        <AnimatedCardContent className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance Score</h3>
          <div className="flex items-center justify-center">
            <div className="relative w-32 h-32">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="12"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke={complianceScore >= 80 ? '#10b981' : complianceScore >= 60 ? '#f59e0b' : '#ef4444'}
                  strokeWidth="12"
                  strokeDasharray={`${complianceScore * 2.51} 251`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-3xl font-bold ${complianceScore >= 80 ? 'text-green-600' : complianceScore >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                  {complianceScore}%
                </span>
              </div>
            </div>
          </div>
          <p className="text-center text-sm text-gray-500 mt-4">
            {complianceScore >= 80 ? 'System healthy' : complianceScore >= 60 ? 'Needs attention' : 'Critical issues'}
          </p>
        </AnimatedCardContent>
      </AnimatedCard>

      {/* Pending Actions */}
      <AnimatedCard variant="elevated" className="lg:col-span-1">
        <AnimatedCardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Pending Actions</h3>
            {dashboard?.pendingApprovals.total ? (
              <span className="px-2 py-1 text-xs font-bold rounded-full bg-amber-100 text-amber-700">
                {dashboard.pendingApprovals.total}
              </span>
            ) : null}
          </div>
          {dashboard?.pendingApprovals.total === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-400">
              <CheckCircleIcon className="h-12 w-12 mb-2 text-green-400" />
              <p className="text-sm">All clear! No pending actions.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {dashboard?.pendingApprovals.usageResets > 0 && (
                <ActionItem
                  icon={<ArrowPathIcon className="h-4 w-4" />}
                  label="Usage reset requests"
                  count={dashboard.pendingApprovals.usageResets}
                  color="amber"
                  onClick={() => onTabChange('approvals')}
                />
              )}
              {dashboard?.pendingApprovals.bulkAssigns > 0 && (
                <ActionItem
                  icon={<BuildingOfficeIcon className="h-4 w-4" />}
                  label="Bulk assign requests"
                  count={dashboard.pendingApprovals.bulkAssigns}
                  color="amber"
                  onClick={() => onTabChange('approvals')}
                />
              )}
              {dashboard?.pendingApprovals.pricingChanges > 0 && (
                <ActionItem
                  icon={<CurrencyDollarIcon className="h-4 w-4" />}
                  label="Pricing changes pending"
                  count={dashboard.pendingApprovals.pricingChanges}
                  color="blue"
                  onClick={() => onTabChange('pricing')}
                />
              )}
            </div>
          )}
        </AnimatedCardContent>
      </AnimatedCard>

      {/* System Health */}
      <AnimatedCard variant="elevated" className="lg:col-span-1">
        <AnimatedCardContent className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">System Health</h3>
          <div className="space-y-3">
            <HealthItem
              label="Reconciliation"
              status={dashboard?.reconciliation.status === 'match' ? 'healthy' : dashboard?.reconciliation.status === 'critical' ? 'critical' : 'warning'}
              detail={dashboard?.reconciliation.lastRun ? 'Last run: ' + new Date(dashboard.reconciliation.lastRun).toLocaleDateString() : 'Never run'}
              onClick={() => onTabChange('reconciliation')}
            />
            <HealthItem
              label="Anomalies"
              status={anomaliesSummary.critical > 0 ? 'critical' : anomaliesSummary.warning > 0 ? 'warning' : 'healthy'}
              detail={`${anomaliesSummary.critical} critical, ${anomaliesSummary.warning} warnings`}
              onClick={() => onTabChange('anomalies')}
            />
            <HealthItem
              label="Partner Quotas"
              status={quotasSummary.atLimit > 0 ? 'critical' : quotasSummary.nearLimit > 0 ? 'warning' : 'healthy'}
              detail={`${quotasSummary.atLimit} at limit, ${quotasSummary.nearLimit} near limit`}
              onClick={() => onTabChange('quotas')}
            />
            <HealthItem
              label="Custom Plans"
              status={customPlansSummary.expired > 0 ? 'critical' : customPlansSummary.needsReview > 0 ? 'warning' : 'healthy'}
              detail={`${customPlansSummary.needsReview} need review`}
              onClick={() => onTabChange('custom-plans')}
            />
          </div>
        </AnimatedCardContent>
      </AnimatedCard>
    </div>
  );
};

// ============================================================================
// TAB: APPROVALS
// ============================================================================

interface ApprovalsTabProps {
  pendingApprovals: ReturnType<typeof useComplianceStore>['pendingApprovals'];
  loading: boolean;
  onApproveReset: (req: UsageResetRequest) => void;
  onRejectReset: (req: UsageResetRequest) => void;
  onApproveBulk: (req: BulkAssignRequest) => void;
  onRejectBulk: (req: BulkAssignRequest) => void;
  formatDate: (date: string) => string;
}

const ApprovalsTab: React.FC<ApprovalsTabProps> = ({
  pendingApprovals,
  loading,
  onApproveReset,
  onRejectReset,
  onApproveBulk,
  onRejectBulk,
  formatDate
}) => {
  if (loading) {
    return <LoadingSpinner />;
  }

  const hasUsageResets = pendingApprovals.usageResets.length > 0;
  const hasBulkAssigns = pendingApprovals.bulkAssigns.length > 0;
  const hasPricingChanges = pendingApprovals.pricingChanges.length > 0;

  if (!hasUsageResets && !hasBulkAssigns && !hasPricingChanges) {
    return (
      <EmptyState
        icon={<CheckCircleIcon className="h-16 w-16 text-green-400" />}
        title="No Pending Approvals"
        description="All approval requests have been processed."
      />
    );
  }

  const resetColumns: ColumnDefinition<UsageResetRequest>[] = [
    { id: 'org', header: 'Organization', accessor: (r) => r.organizationId?.name || 'Unknown' },
    { id: 'period', header: 'Period', accessor: (r) => r.periodKey },
    { id: 'reason', header: 'Reason', accessor: (r) => r.reason, className: 'max-w-xs truncate' },
    { id: 'requestedBy', header: 'Requested By', accessor: (r) => r.requestedBy?.name || 'Unknown' },
    { id: 'requestedAt', header: 'Date', accessor: (r) => formatDate(r.requestedAt) },
    {
      id: 'actions',
      header: 'Actions',
      accessor: () => '',
      render: (_, row) => (
        <div className="flex gap-2">
          <button
            onClick={() => onApproveReset(row)}
            className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200"
          >
            Approve
          </button>
          <button
            onClick={() => onRejectReset(row)}
            className="px-3 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200"
          >
            Reject
          </button>
        </div>
      )
    }
  ];

  const bulkColumns: ColumnDefinition<BulkAssignRequest>[] = [
    { id: 'orgs', header: 'Organizations', accessor: (r) => `${r.organizations?.length || 0} orgs` },
    { id: 'plan', header: 'Plan', accessor: (r) => r.planName || r.planId },
    { id: 'justification', header: 'Justification', accessor: (r) => r.justification, className: 'max-w-xs truncate' },
    { id: 'requestedBy', header: 'Requested By', accessor: (r) => r.requestedBy?.name || 'Unknown' },
    { id: 'requestedAt', header: 'Date', accessor: (r) => formatDate(r.requestedAt) },
    {
      id: 'actions',
      header: 'Actions',
      accessor: () => '',
      render: (_, row) => (
        <div className="flex gap-2">
          <button
            onClick={() => onApproveBulk(row)}
            className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded hover:bg-green-200"
          >
            Approve
          </button>
          <button
            onClick={() => onRejectBulk(row)}
            className="px-3 py-1 text-xs font-medium text-red-700 bg-red-100 rounded hover:bg-red-200"
          >
            Reject
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {hasUsageResets && (
        <AnimatedCard variant="elevated">
          <AnimatedCardContent className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ArrowPathIcon className="h-5 w-5 text-amber-500" />
              Usage Reset Requests
              <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded-full bg-amber-100 text-amber-700">
                {pendingApprovals.usageResets.length}
              </span>
            </h3>
            <DataTable
              data={pendingApprovals.usageResets}
              columns={resetColumns}
              emptyMessage="No usage reset requests"
            />
          </AnimatedCardContent>
        </AnimatedCard>
      )}

      {hasBulkAssigns && (
        <AnimatedCard variant="elevated">
          <AnimatedCardContent className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BuildingOfficeIcon className="h-5 w-5 text-amber-500" />
              Bulk Assignment Requests
              <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded-full bg-amber-100 text-amber-700">
                {pendingApprovals.bulkAssigns.length}
              </span>
            </h3>
            <DataTable
              data={pendingApprovals.bulkAssigns}
              columns={bulkColumns}
              emptyMessage="No bulk assignment requests"
            />
          </AnimatedCardContent>
        </AnimatedCard>
      )}

      {hasPricingChanges && (
        <AnimatedCard variant="elevated">
          <AnimatedCardContent className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CurrencyDollarIcon className="h-5 w-5 text-blue-500" />
              Pricing Changes Awaiting Acknowledgment
              <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded-full bg-blue-100 text-blue-700">
                {pendingApprovals.pricingChanges.length}
              </span>
            </h3>
            <div className="space-y-3">
              {pendingApprovals.pricingChanges.map((change) => (
                <div key={change.id} className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{change.organizationId?.name || change.organizationName}</p>
                      <p className="text-sm text-gray-600">{change.changeImpact.summary}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Effective: {formatDate(change.effectiveDate)}
                      </p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      change.changeImpact.percentageChange > 10 ? 'bg-red-100 text-red-700' :
                      change.changeImpact.percentageChange > 0 ? 'bg-amber-100 text-amber-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {change.changeImpact.percentageChange > 0 ? '+' : ''}{change.changeImpact.percentageChange.toFixed(1)}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </AnimatedCardContent>
        </AnimatedCard>
      )}
    </div>
  );
};

// ============================================================================
// TAB: ANOMALIES
// ============================================================================

interface AnomaliesTabProps {
  anomalies: AnomalyAlert[];
  summary: ReturnType<typeof useComplianceStore>['anomaliesSummary'];
  loading: boolean;
  onViewDetail: (alert: AnomalyAlert) => void;
  getSeverityColor: (severity: 'critical' | 'warning' | 'info') => string;
  formatDate: (date: string) => string;
}

const AnomaliesTab: React.FC<AnomaliesTabProps> = ({
  anomalies,
  summary,
  loading,
  onViewDetail,
  getSeverityColor,
  formatDate
}) => {
  if (loading) {
    return <LoadingSpinner />;
  }

  if (anomalies.length === 0) {
    return (
      <EmptyState
        icon={<CheckCircleIcon className="h-16 w-16 text-green-400" />}
        title="No Anomalies Detected"
        description="All usage patterns are within normal ranges."
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-red-50 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
            <span className="text-sm font-medium text-red-800">Critical</span>
          </div>
          <p className="text-2xl font-bold text-red-900 mt-2">{summary.critical}</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <BellAlertIcon className="h-5 w-5 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">Warning</span>
          </div>
          <p className="text-2xl font-bold text-amber-900 mt-2">{summary.warning}</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <BoltIcon className="h-5 w-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">Info</span>
          </div>
          <p className="text-2xl font-bold text-blue-900 mt-2">{summary.info}</p>
        </div>
      </div>

      {/* Alerts List */}
      <AnimatedCard variant="elevated">
        <AnimatedCardContent className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Detected Anomalies</h3>
          <div className="space-y-3">
            {anomalies.map((alert, idx) => (
              <motion.div
                key={idx}
                className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 cursor-pointer"
                onClick={() => onViewDetail(alert)}
                whileHover={{ scale: 1.01 }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getSeverityColor(alert.severity)}`}>
                      {alert.severity.toUpperCase()}
                    </span>
                    <div>
                      <p className="font-medium text-gray-900">{alert.message}</p>
                      <p className="text-sm text-gray-500">
                        {alert.organizationName || alert.organizationId}
                        {alert.userName && ` - ${alert.userName}`}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">{formatDate(alert.detectedAt)}</p>
                    </div>
                  </div>
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                </div>
              </motion.div>
            ))}
          </div>
        </AnimatedCardContent>
      </AnimatedCard>
    </div>
  );
};

// ============================================================================
// TAB: CUSTOM PLANS
// ============================================================================

interface CustomPlansTabProps {
  plans: CustomPlan[];
  summary: ReturnType<typeof useComplianceStore>['customPlansSummary'];
  loading: boolean;
  formatDate: (date: string) => string;
}

const CustomPlansTab: React.FC<CustomPlansTabProps> = ({
  plans,
  summary,
  loading,
  formatDate
}) => {
  if (loading) {
    return <LoadingSpinner />;
  }

  if (plans.length === 0) {
    return (
      <EmptyState
        icon={<Cog6ToothIcon className="h-16 w-16 text-gray-400" />}
        title="No Custom Plans"
        description="No custom subscription plans are currently active."
      />
    );
  }

  const columns: ColumnDefinition<CustomPlan>[] = [
    { id: 'org', header: 'Organization', accessor: (p) => p.organizationId?.name || 'Unknown' },
    { id: 'plan', header: 'Plan Name', accessor: (p) => p.planName },
    { id: 'limits', header: 'Limits', accessor: (p) => `${p.limits.encountersPerPeriod} enc / ${p.limits.transcriptionMinutesPerPeriod} min` },
    {
      id: 'status',
      header: 'Status',
      accessor: () => '',
      render: (_, row) => {
        const now = new Date();
        const expired = row.customPlanDetails?.expiresAt && new Date(row.customPlanDetails.expiresAt) < now;
        const needsReview = row.customPlanDetails?.nextReviewAt && new Date(row.customPlanDetails.nextReviewAt) <= now;

        if (expired) return <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-700">Expired</span>;
        if (needsReview) return <span className="px-2 py-1 text-xs font-medium rounded bg-amber-100 text-amber-700">Review Due</span>;
        return <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-700">Active</span>;
      }
    },
    { id: 'nextReview', header: 'Next Review', accessor: (p) => p.customPlanDetails?.nextReviewAt ? formatDate(p.customPlanDetails.nextReviewAt) : 'N/A' },
    { id: 'approvedBy', header: 'Approved By', accessor: (p) => p.customPlanDetails?.approvedBy?.name || 'N/A' }
  ];

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-sm font-medium text-green-800">Active</p>
          <p className="text-2xl font-bold text-green-900">{summary.active}</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-4">
          <p className="text-sm font-medium text-amber-800">Needs Review</p>
          <p className="text-2xl font-bold text-amber-900">{summary.needsReview}</p>
        </div>
        <div className="bg-red-50 rounded-lg p-4">
          <p className="text-sm font-medium text-red-800">Expired</p>
          <p className="text-2xl font-bold text-red-900">{summary.expired}</p>
        </div>
      </div>

      <AnimatedCard variant="elevated">
        <AnimatedCardContent className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Custom Plans</h3>
          <DataTable
            data={plans}
            columns={columns}
            emptyMessage="No custom plans"
          />
        </AnimatedCardContent>
      </AnimatedCard>
    </div>
  );
};

// ============================================================================
// TAB: PARTNER QUOTAS
// ============================================================================

interface PartnerQuotasTabProps {
  quotas: PartnerQuota[];
  summary: ReturnType<typeof useComplianceStore>['quotasSummary'];
  loading: boolean;
}

const PartnerQuotasTab: React.FC<PartnerQuotasTabProps> = ({
  quotas,
  summary,
  loading
}) => {
  if (loading) {
    return <LoadingSpinner />;
  }

  if (quotas.length === 0) {
    return (
      <EmptyState
        icon={<UserGroupIcon className="h-16 w-16 text-gray-400" />}
        title="No Partners"
        description="No channel partners are registered."
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-sm font-medium text-green-800">Healthy</p>
          <p className="text-2xl font-bold text-green-900">{summary.healthy}</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-4">
          <p className="text-sm font-medium text-amber-800">Near Limit (80%+)</p>
          <p className="text-2xl font-bold text-amber-900">{summary.nearLimit}</p>
        </div>
        <div className="bg-red-50 rounded-lg p-4">
          <p className="text-sm font-medium text-red-800">At Limit</p>
          <p className="text-2xl font-bold text-red-900">{summary.atLimit}</p>
        </div>
      </div>

      <AnimatedCard variant="elevated">
        <AnimatedCardContent className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Partner Quota Status</h3>
          <div className="space-y-3">
            {quotas.map((partner) => (
              <div
                key={partner.id}
                className={`p-4 rounded-lg border ${
                  partner.quotaStatus.isAtLimit ? 'border-red-200 bg-red-50' :
                  partner.quotaStatus.isNearLimit ? 'border-amber-200 bg-amber-50' :
                  'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{partner.name}</p>
                    <p className="text-sm text-gray-500">
                      {partner.quotas?.currentActiveOrgs || 0} / {partner.quotas?.maxOrganizations === -1 ? 'Unlimited' : partner.quotas?.maxOrganizations || 0} organizations
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          partner.quotaStatus.isAtLimit ? 'bg-red-500' :
                          partner.quotaStatus.isNearLimit ? 'bg-amber-500' :
                          'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(partner.quotaStatus.percentUsed, 100)}%` }}
                      />
                    </div>
                    <span className={`text-sm font-medium ${
                      partner.quotaStatus.isAtLimit ? 'text-red-700' :
                      partner.quotaStatus.isNearLimit ? 'text-amber-700' :
                      'text-green-700'
                    }`}>
                      {partner.quotaStatus.percentUsed}%
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </AnimatedCardContent>
      </AnimatedCard>
    </div>
  );
};

// ============================================================================
// TAB: RECONCILIATION
// ============================================================================

interface ReconciliationTabProps {
  status: ReturnType<typeof useComplianceStore>['reconciliationStatus'];
  loading: boolean;
  onTrigger: () => void;
  getStatusColor: (status: ReconciliationStatus) => string;
  formatDate: (date: string) => string;
}

const ReconciliationTab: React.FC<ReconciliationTabProps> = ({
  status,
  loading,
  onTrigger,
  getStatusColor,
  formatDate
}) => {
  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Card */}
        <AnimatedCard variant="elevated">
          <AnimatedCardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Reconciliation Status</h3>
              <motion.button
                onClick={onTrigger}
                disabled={status?.isRunning}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {status?.isRunning ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <PlayIcon className="h-4 w-4" />
                    Run Now
                  </>
                )}
              </motion.button>
            </div>

            {status?.lastResult ? (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 text-sm font-medium rounded ${getStatusColor(status.lastResult.status)}`}>
                    {status.lastResult.status.replace('_', ' ').toUpperCase()}
                  </span>
                  <span className="text-sm text-gray-500">
                    {formatDate(status.lastResult.reconciliationDate)}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{status.lastResult.statusMessage}</p>

                <div className="grid grid-cols-2 gap-4 pt-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Token Variance</p>
                    <p className={`text-2xl font-bold ${status.lastResult.tokenVariancePercent > 5 ? 'text-red-600' : 'text-gray-900'}`}>
                      {status.lastResult.tokenVariancePercent.toFixed(2)}%
                    </p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Minute Variance</p>
                    <p className={`text-2xl font-bold ${status.lastResult.minuteVariancePercent > 5 ? 'text-red-600' : 'text-gray-900'}`}>
                      {status.lastResult.minuteVariancePercent.toFixed(2)}%
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <ScaleIcon className="h-12 w-12 mb-2" />
                <p className="text-sm">No reconciliation has been run yet</p>
              </div>
            )}
          </AnimatedCardContent>
        </AnimatedCard>

        {/* Next Run Info */}
        <AnimatedCard variant="elevated">
          <AnimatedCardContent className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Schedule</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg">
                <CalendarDaysIcon className="h-6 w-6 text-purple-600" />
                <div>
                  <p className="font-medium text-purple-900">Next Scheduled Run</p>
                  <p className="text-sm text-purple-700">{status?.nextScheduledRun || 'First of next month at 02:00 UTC'}</p>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Variance Thresholds</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Minor variance</span>
                    <span className="text-green-600">&le; 2%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Major variance</span>
                    <span className="text-amber-600">&le; 5%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Critical variance</span>
                    <span className="text-red-600">&gt; 10%</span>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedCardContent>
        </AnimatedCard>
      </div>

      {/* Details */}
      {status?.lastResult?.details && (
        <AnimatedCard variant="elevated">
          <AnimatedCardContent className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Breakdown</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(status.lastResult.details.byService).map(([service, data]) => (
                <div key={service} className="p-4 border border-gray-200 rounded-lg">
                  <p className="font-medium text-gray-900">{service}</p>
                  <div className="mt-2 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">MongoDB</span>
                      <span>{data.mongodb.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Azure</span>
                      <span>{data.azure.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between font-medium">
                      <span className={data.variancePercent > 5 ? 'text-red-600' : 'text-gray-700'}>Variance</span>
                      <span className={data.variancePercent > 5 ? 'text-red-600' : 'text-gray-700'}>
                        {data.variancePercent.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </AnimatedCardContent>
        </AnimatedCard>
      )}
    </div>
  );
};

// ============================================================================
// TAB: PRICING CHANGES
// ============================================================================

interface PricingChangesTabProps {
  changes: PricingChangeNotification[];
  summary: ReturnType<typeof useComplianceStore>['pricingChangesSummary'];
  loading: boolean;
  formatDate: (date: string) => string;
}

const PricingChangesTab: React.FC<PricingChangesTabProps> = ({
  changes,
  summary,
  loading,
  formatDate
}) => {
  if (loading) {
    return <LoadingSpinner />;
  }

  if (changes.length === 0) {
    return (
      <EmptyState
        icon={<CurrencyDollarIcon className="h-16 w-16 text-gray-400" />}
        title="No Pricing Changes"
        description="No pricing change notifications are pending."
      />
    );
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-amber-100 text-amber-700';
      case 'acknowledged':
        return 'bg-blue-100 text-blue-700';
      case 'applied':
        return 'bg-green-100 text-green-700';
      case 'cancelled':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-amber-50 rounded-lg p-4">
          <p className="text-sm font-medium text-amber-800">Pending</p>
          <p className="text-2xl font-bold text-amber-900">{summary.pending}</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-sm font-medium text-blue-800">Acknowledged</p>
          <p className="text-2xl font-bold text-blue-900">{summary.acknowledged}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-sm font-medium text-green-800">Applied</p>
          <p className="text-2xl font-bold text-green-900">{summary.applied}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <p className="text-sm font-medium text-gray-800">Cancelled</p>
          <p className="text-2xl font-bold text-gray-900">{summary.cancelled}</p>
        </div>
        <div className="bg-red-50 rounded-lg p-4">
          <p className="text-sm font-medium text-red-800">Overdue</p>
          <p className="text-2xl font-bold text-red-900">{summary.overdue}</p>
        </div>
      </div>

      <AnimatedCard variant="elevated">
        <AnimatedCardContent className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Pricing Notifications</h3>
          <div className="space-y-4">
            {changes.map((change) => (
              <div key={change.id} className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <p className="font-medium text-gray-900">
                        {change.organizationId?.name || change.organizationName}
                      </p>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded ${getStatusBadge(change.status)}`}>
                        {change.status.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{change.changeImpact.summary}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>Notified: {formatDate(change.notifiedAt)}</span>
                      <span>Effective: {formatDate(change.effectiveDate)}</span>
                      {change.acknowledgedAt && (
                        <span>Acknowledged: {formatDate(change.acknowledgedAt)}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-lg font-bold ${
                      change.changeImpact.percentageChange > 0 ? 'text-red-600' : 'text-green-600'
                    }`}>
                      {change.changeImpact.percentageChange > 0 ? (
                        <ArrowTrendingUpIcon className="inline h-4 w-4 mr-1" />
                      ) : (
                        <ArrowTrendingDownIcon className="inline h-4 w-4 mr-1" />
                      )}
                      {Math.abs(change.changeImpact.percentageChange).toFixed(1)}%
                    </p>
                    <p className="text-xs text-gray-500">
                      ${change.currentPricing.monthlyPerClinicalUser} → ${change.newPricing.monthlyPerClinicalUser}/user
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </AnimatedCardContent>
      </AnimatedCard>
    </div>
  );
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const LoadingSpinner: React.FC = () => (
  <div className="flex items-center justify-center h-64">
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
    >
      <div className="h-8 w-8 border-4 border-purple-600 border-t-transparent rounded-full" />
    </motion.div>
  </div>
);

interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({ icon, title, description }) => (
  <div className="flex flex-col items-center justify-center py-16">
    {icon}
    <h3 className="mt-4 text-lg font-medium text-gray-900">{title}</h3>
    <p className="mt-1 text-sm text-gray-500">{description}</p>
  </div>
);

interface ActionItemProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  color: string;
  onClick?: () => void;
}

const ActionItem: React.FC<ActionItemProps> = ({ icon, label, count, color, onClick }) => {
  const colorClasses: Record<string, { bg: string; text: string; badge: string }> = {
    amber: { bg: 'bg-amber-50', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-800' },
    red: { bg: 'bg-red-50', text: 'text-red-700', badge: 'bg-red-100 text-red-800' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-800' }
  };

  const classes = colorClasses[color] || colorClasses.amber;

  return (
    <motion.div
      className={`flex items-center justify-between p-3 rounded-lg ${classes.bg} cursor-pointer`}
      onClick={onClick}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="flex items-center gap-2">
        <span className={classes.text}>{icon}</span>
        <span className={`text-sm font-medium ${classes.text}`}>{label}</span>
      </div>
      <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${classes.badge}`}>
        {count}
      </span>
    </motion.div>
  );
};

interface HealthItemProps {
  label: string;
  status: 'healthy' | 'warning' | 'critical';
  detail: string;
  onClick?: () => void;
}

const HealthItem: React.FC<HealthItemProps> = ({ label, status, detail, onClick }) => {
  const statusColors = {
    healthy: 'text-green-600 bg-green-100',
    warning: 'text-amber-600 bg-amber-100',
    critical: 'text-red-600 bg-red-100'
  };

  const statusIcons = {
    healthy: <CheckCircleIcon className="h-4 w-4" />,
    warning: <ExclamationTriangleIcon className="h-4 w-4" />,
    critical: <XCircleIcon className="h-4 w-4" />
  };

  return (
    <motion.div
      className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 cursor-pointer"
      onClick={onClick}
      whileHover={{ x: 4 }}
    >
      <div className="flex items-center gap-3">
        <span className={`p-1 rounded ${statusColors[status]}`}>
          {statusIcons[status]}
        </span>
        <div>
          <p className="text-sm font-medium text-gray-900">{label}</p>
          <p className="text-xs text-gray-500">{detail}</p>
        </div>
      </div>
    </motion.div>
  );
};

// ============================================================================
// MODALS
// ============================================================================

interface ConfirmModalProps {
  title: string;
  message: string;
  confirmLabel: string;
  confirmVariant: 'success' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  title,
  message,
  confirmLabel,
  confirmVariant,
  onConfirm,
  onCancel
}) => (
  <motion.div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={onCancel}
  >
    <motion.div
      className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600 mb-6">{message}</p>
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className={`px-4 py-2 text-sm font-medium text-white rounded-lg ${
            confirmVariant === 'success' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
          }`}
        >
          {confirmLabel}
        </button>
      </div>
    </motion.div>
  </motion.div>
);

interface RejectModalProps {
  title: string;
  message: string;
  reason: string;
  onReasonChange: (reason: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const RejectModal: React.FC<RejectModalProps> = ({
  title,
  message,
  reason,
  onReasonChange,
  onConfirm,
  onCancel
}) => (
  <motion.div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={onCancel}
  >
    <motion.div
      className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-600 mb-4">{message}</p>
      <textarea
        value={reason}
        onChange={(e) => onReasonChange(e.target.value)}
        placeholder="Enter rejection reason..."
        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
        rows={3}
      />
      <div className="flex justify-end gap-3 mt-4">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={!reason.trim()}
          className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Reject
        </button>
      </div>
    </motion.div>
  </motion.div>
);

interface AnomalyDetailModalProps {
  alert: AnomalyAlert;
  onClose: () => void;
  getSeverityColor: (severity: 'critical' | 'warning' | 'info') => string;
}

const AnomalyDetailModal: React.FC<AnomalyDetailModalProps> = ({
  alert,
  onClose,
  getSeverityColor
}) => (
  <motion.div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={onClose}
  >
    <motion.div
      className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6"
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 text-sm font-medium rounded ${getSeverityColor(alert.severity)}`}>
            {alert.severity.toUpperCase()}
          </span>
          <span className="text-sm text-gray-500">{alert.type.replace('_', ' ')}</span>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <h3 className="text-lg font-semibold text-gray-900 mb-2">{alert.message}</h3>

      <div className="space-y-3 text-sm">
        <div className="flex justify-between p-3 bg-gray-50 rounded">
          <span className="text-gray-600">Organization</span>
          <span className="font-medium">{alert.organizationName || alert.organizationId}</span>
        </div>
        {alert.userName && (
          <div className="flex justify-between p-3 bg-gray-50 rounded">
            <span className="text-gray-600">User</span>
            <span className="font-medium">{alert.userName}</span>
          </div>
        )}
        <div className="flex justify-between p-3 bg-gray-50 rounded">
          <span className="text-gray-600">Baseline</span>
          <span className="font-medium">{alert.details.baseline.toLocaleString()}</span>
        </div>
        <div className="flex justify-between p-3 bg-gray-50 rounded">
          <span className="text-gray-600">Current</span>
          <span className="font-medium">{alert.details.current.toLocaleString()}</span>
        </div>
        <div className="flex justify-between p-3 bg-gray-50 rounded">
          <span className="text-gray-600">Multiplier</span>
          <span className="font-medium text-red-600">{alert.details.multiplier.toFixed(1)}x</span>
        </div>
        <div className="flex justify-between p-3 bg-gray-50 rounded">
          <span className="text-gray-600">Time Window</span>
          <span className="font-medium">{alert.details.timeWindow}</span>
        </div>
      </div>

      {alert.recommendedAction && (
        <div className="mt-4 p-3 bg-purple-50 rounded-lg">
          <p className="text-sm font-medium text-purple-900">Recommended Action</p>
          <p className="text-sm text-purple-700 mt-1">{alert.recommendedAction}</p>
        </div>
      )}

      <div className="flex justify-end mt-6">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          Close
        </button>
      </div>
    </motion.div>
  </motion.div>
);

interface TriggerReconciliationModalProps {
  period: string;
  onPeriodChange: (period: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isRunning: boolean;
}

const TriggerReconciliationModal: React.FC<TriggerReconciliationModalProps> = ({
  period,
  onPeriodChange,
  onConfirm,
  onCancel,
  isRunning
}) => {
  // Generate last 12 months as options
  const monthOptions: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 1; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    monthOptions.push({ value, label });
  }

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onCancel}
    >
      <motion.div
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Trigger Reconciliation</h3>
        <p className="text-sm text-gray-600 mb-4">
          Select a period to reconcile. Leave empty to reconcile the previous month.
        </p>

        <select
          value={period}
          onChange={(e) => onPeriodChange(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="">Previous month (default)</option>
          {monthOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isRunning}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRunning ? 'Running...' : 'Start Reconciliation'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ComplianceDashboard;
